import { Button, Icon } from '@wtfalch/design';
import { useState } from 'react';

import { dueLabel } from './dates.ts';
import { Quick } from './Quick.tsx';
import { Who } from './Rows.tsx';
import type { Task, TaskList, TaskStatus } from './source.ts';

/** The three groups people work in always get a column; backlog and cancelled
 *  only when something is in them. */
export function boardColumns(list: TaskList, tasks: readonly Task[]): TaskStatus[] {
  return list.statuses.filter(
    (s) =>
      s.group === 'todo' ||
      s.group === 'started' ||
      s.group === 'done' ||
      tasks.some((t) => t.statusId === s.id),
  );
}

/** A shared list as columns: the list's own statuses, a card each, drag to move. */
export function ListBoard({
  list,
  tasks,
  today,
  onOpen,
  onMove,
  onAdd,
}: {
  list: TaskList;
  tasks: readonly Task[];
  today: string;
  onOpen: (task: Task) => void;
  onMove: (task: Task, statusId: string) => Promise<void>;
  onAdd: (statusId: string, task: { title: string; dueOn: string | null }) => Promise<void>;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const columns = boardColumns(list, tasks);

  return (
    <div className="tasks-board">
      {columns.map((column, index) => {
        const cards = tasks.filter((t) => t.statusId === column.id);
        return (
          <section
            key={column.id}
            className="tasks-column"
            aria-label={column.name}
            data-over={over === column.id || undefined}
            onDragOver={(e) => {
              if (dragging) {
                e.preventDefault();
                setOver(column.id);
              }
            }}
            onDragLeave={() => setOver((o) => (o === column.id ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              const task = tasks.find((t) => t.id === dragging);
              setDragging(null);
              setOver(null);
              if (task && task.statusId !== column.id) void onMove(task, column.id);
            }}
          >
            <h3 className="tasks-group-head">
              {column.name}
              <span className="tasks-count">{cards.length}</span>
            </h3>
            <ul className="tasks-cards">
              {cards.map((task) => (
                <li key={task.id}>
                  <button
                    type="button"
                    draggable
                    className="tasks-card"
                    aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
                    onClick={() => onOpen(task)}
                    onDragStart={() => setDragging(task.id)}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    onKeyDown={(e) => {
                      const step = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
                      const target = columns[index + step];
                      if (e.altKey && step !== 0 && target) {
                        e.preventDefault();
                        void onMove(task, target.id);
                      }
                    }}
                  >
                    <span className="tasks-card-title">{task.title}</span>
                    <span className="tasks-card-foot">
                      <Who person={task.assignee} size="sm" />
                      {task.dueOn && (
                        <span className="tasks-row-when">{dueLabel(task.dueOn, today)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {adding === column.id ? (
              <Quick
                label={`Add to ${column.name}`}
                placeholder={`Add to ${column.name}`}
                today={today}
                onAdd={async (task) => {
                  await onAdd(column.id, task);
                }}
              />
            ) : (
              <Button kind="ghost" size="sm" onPress={() => setAdding(column.id)}>
                <Icon name="plus" size={14} />
                Add to {column.name}
              </Button>
            )}
          </section>
        );
      })}
    </div>
  );
}
